import { create } from 'zustand';
import { useEffect } from 'react';
import { Category } from '../../types/RoomCategory';
import { Amenity } from '../../types/Amenity'; // Assuming Amenity type exists; adjust path if needed
import { Rooms } from '../../types/Rooms';
import { RoomType } from '../../types/RoomTypes';

// Extendable interfaces for other entities
interface User { id: string; name: string; /* ... */ }
interface Booking { id: number; status: string; /* ... */ }
// Add more: interface Inventory { id: number; quantity: number; }

interface State {
  roomCategories: Category[];
  amenities: Amenity[];
  rooms: Rooms[];
  roomTypes: RoomType[];
  // Extendable slices
  users: User[];
  bookings: Booking[];
  // Add more: inventory: Inventory[];

  setRoomCategories: (categories: Category[]) => void;
  setAmenities: (amenities: Amenity[]) => void;
  setRoomData: (rooms: Rooms[]) => void;
  setRoomTypes: (roomTypes: RoomType[]) => void;
  // Extendable setters
  setUsers: (users: User[]) => void;
  setBookings: (bookings: Booking[]) => void;
  // Add more: setInventory: (inventory: Inventory[]) => void;
}

// Scalable event handler map
const eventHandlers: Record<string, (state: State, payload: any) => void> = {
  roomcategorycreated: (state, payload: Category) => {
    state.roomCategories = [payload, ...state.roomCategories];
  },
  roomcategoryupdated: (state, payload: Category) => {
    state.roomCategories = state.roomCategories.map(cat =>
      cat.categoryId === payload.categoryId ? payload : cat
    );
  },
  roomcategorydeleted: (state, payload: { categoryId: string }) => {
    state.roomCategories = state.roomCategories.filter(
      cat => String(cat.categoryId) !== String(payload.categoryId)
    );
  },
  amenitycreated: (state, payload: Amenity) => {
    state.amenities = [payload, ...state.amenities];
  },
  amenityupdated: (state, payload: Amenity) => {
    state.amenities = state.amenities.map(a =>
      a.amenityId === payload.amenityId ? payload : a
    );
  },
  amenitydeleted: (state, payload: { amenityId: number }) => {
    state.amenities = state.amenities.filter(
      a => a.amenityId !== payload.amenityId
    );
    // Optional cross-update: If roomCategories embed or reference amenities, clean them up here
    // Example (uncomment/adjust if Category has amenities array):
    // state.roomCategories = state.roomCategories.map(cat => ({
    //   ...cat,
    //   amenities: cat.amenities?.filter(a => a.amenityId !== payload.amenityId) || []
    // }));
  },


  // Rooms – full CRUD real-time handlers
roomcreated: (state, payload: Rooms) => {
  state.rooms = [payload, ...state.rooms];
},

roomupdated: (state, payload: Rooms) => {
  state.rooms = state.rooms.map(room =>
    room.roomId === payload.roomId ? payload : room
  );
},

roomdeleted: (state, payload: { roomId: string | number }) => {
  state.rooms = state.rooms.filter(
    room => room.roomId !== payload.roomId
  );
},

// Optional: if your backend sends bulk replace (rare but possible)
roomsreplaced: (state, payload: Rooms[]) => {
  state.rooms = payload;
},

// Optional: if you get a "room status changed" event (e.g. cleaned, occupied, maintenance)
roomstatuschanged: (state, payload: { roomId: string | number; status: string }) => {
  state.rooms = state.rooms.map(room =>
    room.roomId === payload.roomId
      ? { ...room, status: payload.status }
      : room
  );
},
  // Extendable handlers for new entities
  usercreated: (state, payload: User) => {
    state.users = [payload, ...state.users];
  },
  userupdated: (state, payload: User) => {
    state.users = state.users.map(u =>
      u.id === payload.id ? payload : u
    );
  },
  userdeleted: (state, payload: { id: string }) => {
    state.users = state.users.filter(u => u.id !== payload.id);
    // Optional: Cross-updates if users affect other slices (e.g., bookings)
  },
  bookingcreated: (state, payload: Booking) => {
    state.bookings = [payload, ...state.bookings];
  },
  bookingupdated: (state, payload: Booking) => {
    state.bookings = state.bookings.map(b =>
      b.id === payload.id ? payload : b
    );
  },
  bookingdeleted: (state, payload: { id: number }) => {
    state.bookings = state.bookings.filter(b => b.id !== payload.id);
  },
  // Add more handlers: inventoryupdated: (state, payload: Inventory) => { ... },
  roomtypecreated: (state, payload: RoomType) => {
    state.roomTypes = [payload, ...state.roomTypes];
  },

  roomtypeupdated: (state, payload: RoomType) => {
    state.roomTypes = state.roomTypes.map(rt =>
      rt.roomTypeId === payload.roomTypeId ? payload : rt
    );
  },
  roomtypedeleted: (state, payload: { roomTypeId: number }) => {
    state.roomTypes = state.roomTypes.filter(
      rt => rt.roomTypeId !== payload.roomTypeId
    );
  },

  
};

export const useSignalRStore = create<State>((set) => ({
  roomCategories: [],
  amenities: [],
  rooms: [],
  roomTypes: [],
  setRoomCategories: (categories) => set({ roomCategories: categories }),
  setAmenities: (amenities) => set({ amenities }),
  setRoomData: (rooms) => set({ rooms }),
  setRoomTypes: (roomTypes) => set({ roomTypes }),
  // Extendable initial state and setters
  users: [],
  bookings: [],
  // Add more: inventory: [],

  setUsers: (users) => set({ users }),
  setBookings: (bookings) => set({ bookings }),
  // Add more: setInventory: (inventory) => set({ inventory }),
}));

// Global listener for SignalR events via BroadcastChannel (unchanged)
export const SignalRStoreListener = () => {
  useEffect(() => {
    const channel = new BroadcastChannel("signalr-events");

    const handleMessage = (event: MessageEvent) => {
      console.log("📥 [BroadcastChannel] Raw event received:", event);

      const { type, payload } = event.data;
      console.log(`📡 [SignalRStoreListener] Event type: ${type}`);
      console.log("📦 [SignalRStoreListener] Payload:", payload);

      const handler = eventHandlers[type];

      if (handler) {
        console.log(`✅ [SignalRStoreListener] Found handler for: ${type}`);
        useSignalRStore.setState((state) => {
          const newState = { ...state };
          console.log("🧠 [SignalRStoreListener] State before mutation:", newState);
          handler(newState, payload);
          console.log("🧠 [SignalRStoreListener] State after mutation:", newState);
          return newState;
        });
      } else {
        console.warn(`⚠️ [SignalRStoreListener] No handler found for event type: ${type}`);
      }
    };

    channel.addEventListener("message", handleMessage);

    console.log("🔌 [SignalRStoreListener] BroadcastChannel listener mounted");

    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      console.log("❌ [SignalRStoreListener] BroadcastChannel listener unmounted");
    };
  }, []);

  return null;
};